import { useEffect, useMemo, useRef, useState } from "react";

import { LocationOn } from "@mui/icons-material";
import {
  TextField,
  Autocomplete,
  Grid,
  Typography,
  Box,
  debounce,
} from "@mui/material";
import parse from "autosuggest-highlight/parse";

import { LOCATION_CLASSIFICATION } from "@/components/alerts/constants";
import { PlaceType } from "@/types";

interface Props {
  setSelectedAddress: any;
  selectedAddress: PlaceType | null;
  required?: boolean;
  error?: boolean;
  language?: string;
  componentRestrictions?: { country: string[] };
  minLengthAutocomplete?: number;
  index?: number | null;
  label?: string;
  /**
   * Needed when several of these are on one page: the id is what ties the MUI label to its input, so
   * without a distinct one per instance every label points at the first field.
   */
  id?: string;
  /** Overridable so callers can supply a translated one; the default is the original English. */
  placeholder?: string;
  /** Shown under the input. Callers that pass `error` supply the reason through this. */
  helperText?: string;
}

const GooglePlacesAutocomplete: React.FC<Props> = ({
  language = "en",
  componentRestrictions = { country: ["us", "ca"] },
  minLengthAutocomplete = 3,
  required = true,
  error = false,
  label = "Incident Address",
  selectedAddress = null,
  index = null,
  setSelectedAddress = null,
  id = "google-map-autocomplete",
  placeholder = "Start typing",
  helperText,
}) => {
  const [value, setValue] = useState<PlaceType | null>(selectedAddress);
  const [inputValue, setInputValue] = useState(
    selectedAddress?.description && !selectedAddress?.place_id
      ? selectedAddress.description
      : "",
  );
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const loaded = useRef(false);
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const sessionToken =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  // Initialize the autocomplete service only when Google Maps API is available
  useEffect(() => {
    const initializeService = () => {
      if (
        typeof window !== "undefined" &&
        (window as any).google &&
        (window as any).google.maps &&
        (window as any).google.maps.places &&
        !autocompleteService.current
      ) {
        try {
          autocompleteService.current = new (
            window as any
          ).google.maps.places.AutocompleteService();
          placesService.current = new (
            window as any
          ).google.maps.places.PlacesService(document.createElement("div"));
          sessionToken.current = new (
            window as any
          ).google.maps.places.AutocompleteSessionToken();
          loaded.current = true;
        } catch (error) {
          console.error(
            "Failed to initialize Google Places AutocompleteService:",
            error,
          );
        }
      }
    };

    initializeService();

    // If service is not ready, check periodically
    if (!autocompleteService.current) {
      const interval = setInterval(() => {
        initializeService();
        if (autocompleteService.current) {
          clearInterval(interval);
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, []);

  const fetch = useMemo(
    () =>
      debounce(
        (
          request: {
            input: string;
            language: string;
            componentRestrictions: { country: string[] };
            sessionToken?: google.maps.places.AutocompleteSessionToken;
          },
          callback: (results?: readonly PlaceType[]) => void,
        ) => {
          if (autocompleteService.current) {
            (autocompleteService.current as any).getPlacePredictions(
              request,
              callback,
            );
          }
        },
        400,
      ),
    [],
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    if (inputValue.length < minLengthAutocomplete) {
      setOptions(value ? [value] : []);
      return undefined;
    }

    if (!sessionToken.current) {
      sessionToken.current = new google.maps.places.AutocompleteSessionToken();
    }

    fetch(
      {
        input: inputValue,
        language: language,
        componentRestrictions: componentRestrictions,
        sessionToken: sessionToken.current,
      },
      (results?: readonly PlaceType[]) => {
        if (active) {
          let newOptions: readonly PlaceType[] = [];

          if (value) {
            newOptions = [value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      },
    );

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, language, minLengthAutocomplete]);

  return (
    <Autocomplete
      id={id}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      fullWidth
      value={value}
      noOptionsText="No locations"
      onChange={(_event: any, newValue: PlaceType | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if (index !== null) {
          setSelectedAddress((prevAddresses) => {
            const newAddressFields = [...prevAddresses];
            const existingAddress = prevAddresses[index];
            newAddressFields[index] = {
              ...existingAddress,
              ...newValue,
              location_classification:
                existingAddress?.location_classification ||
                LOCATION_CLASSIFICATION.INCIDENT_ADDRESS,
            };
            return newAddressFields;
          });
        } else {
          setSelectedAddress(newValue);
        }
        // Complete the autocomplete billing session with a minimal
        // Place Details call so all preceding predictions are free.
        if (
          newValue?.place_id &&
          placesService.current &&
          sessionToken.current
        ) {
          placesService.current.getDetails(
            {
              placeId: newValue.place_id,
              fields: ["place_id"],
              sessionToken: sessionToken.current,
            },
            () => {
              sessionToken.current =
                new google.maps.places.AutocompleteSessionToken();
            },
          );
        } else {
          // Selection cleared — abandon session and reset token
          sessionToken.current =
            new google.maps.places.AutocompleteSessionToken();
        }
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          fullWidth
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
        />
      )}
      renderOption={(props, option) => {
        // MUI v7 puts `key` inside props; React requires it be passed directly, not spread.
        const { key, ...optionProps } = props;
        const matches =
          option.structured_formatting?.main_text_matched_substrings || [];
        const parts = parse(
          option.structured_formatting?.main_text || "",
          matches.map((match: any) => [
            match.offset,
            match.offset + match.length,
          ]),
        );

        return (
          <li key={key} {...optionProps}>
            <Grid container alignItems="center">
              <Grid sx={{ display: "flex", width: 44 }}>
                <LocationOn sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}>
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {option.structured_formatting?.secondary_text || ""}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};

export default GooglePlacesAutocomplete;
